import { DateOfBirthInput } from '_atoms';
import { useAppSelector } from '_hooks';
import { HelperMethods } from '_utils';
import React, { useState } from 'react';
import {
  MdChildCare,
  MdPerson,
  MdPersonOutline,
  MdWarning,
} from 'react-icons/md';

import { SkaterInfo, TimeSlot } from '@types';

interface SkatersInfoProps {
  skatersInfo?: SkaterInfo[];
  selectedTimeSlot?: TimeSlot;
  onNext: (data?: any) => void;
  onPrevious?: () => void;
  onStepComplete: (data: any) => void;
  limitAdults?: boolean; // Optional prop to limit to 1 adult
}

interface TicketQuantities {
  adults: number;
  children: number;
}

interface AccountContactInfo {
  email: string;
  phoneNumber: string;
}

/** Normalize phone to 10 digits for SkatersInfo validation/input. */
const normalizePhoneNumber = (phone?: string | null): string => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  if (digits.length === 10) {
    return digits;
  }
  return digits;
};

const resolveAccountContact = (
  isAuthenticated: boolean,
  accountDetails: any,
  currentUser: any,
): AccountContactInfo => {
  if (!isAuthenticated) {
    return { email: '', phoneNumber: '' };
  }

  const email = accountDetails?.email || currentUser?.attributes?.email || '';
  const rawPhone =
    accountDetails?.phoneNumber || currentUser?.attributes?.phone_number || '';

  return {
    email,
    phoneNumber: normalizePhoneNumber(rawPhone),
  };
};

// Age validation helper functions
const validateAdultAge = (
  dateOfBirth: string,
): { isValid: boolean; message: string } => {
  if (!dateOfBirth) {
    return { isValid: false, message: 'Date of birth is required' };
  }

  if (!HelperMethods.isPlausibleDateOfBirth(dateOfBirth)) {
    return {
      isValid: false,
      message:
        HelperMethods.getDateOfBirthError(dateOfBirth) ||
        'Enter a valid date of birth',
    };
  }

  const age = HelperMethods.calculateAge(dateOfBirth);
  if (age < 13) {
    return {
      isValid: false,
      message: `Must be 13 or older to purchase adult ticket. Current age: ${age}`,
    };
  }

  return { isValid: true, message: '' };
};

const validateChildAge = (
  dateOfBirth: string,
): { isValid: boolean; message: string } => {
  if (!dateOfBirth) {
    return { isValid: false, message: 'Date of birth is required' };
  }

  if (!HelperMethods.isPlausibleDateOfBirth(dateOfBirth)) {
    return {
      isValid: false,
      message:
        HelperMethods.getDateOfBirthError(dateOfBirth) ||
        'Enter a valid date of birth',
    };
  }

  const age = HelperMethods.calculateAge(dateOfBirth);
  if (age < 3) {
    return {
      isValid: false,
      message: `Must be at least 3 years old. Current age: ${age}`,
    };
  }
  if (age >= 13) {
    return {
      isValid: false,
      message: `Must be under 13 to purchase child ticket. Current age: ${age}`,
    };
  }

  return { isValid: true, message: '' };
};

// Validation function to check if age matches the selected type
const validateAgeMatchesType = (
  dateOfBirth: string,
  isMinor: boolean,
): { isValid: boolean; message: string } => {
  if (!dateOfBirth) {
    return { isValid: true, message: '' }; // Let other validations handle empty dates
  }

  if (!HelperMethods.isPlausibleDateOfBirth(dateOfBirth)) {
    return {
      isValid: false,
      message:
        HelperMethods.getDateOfBirthError(dateOfBirth) ||
        'Enter a valid date of birth',
    };
  }

  const age = HelperMethods.calculateAge(dateOfBirth);

  if (isMinor && age >= 13) {
    return {
      isValid: false,
      message: `Age ${age} is too old for a child ticket. Child tickets are for ages 3-12.`,
    };
  }

  if (!isMinor && age < 13) {
    return {
      isValid: false,
      message: `Age ${age} is too young for an adult ticket. Adult tickets are for ages 13 and older.`,
    };
  }

  return { isValid: true, message: '' };
};

export const SkatersInfo: React.FC<SkatersInfoProps> = ({
  skatersInfo = [],
  selectedTimeSlot,
  onNext,
  onPrevious,
  onStepComplete,
  limitAdults = false,
}) => {
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const accountDetails = useAppSelector(state => state.auth.accountDetails);
  const currentUser = useAppSelector(state => state.auth.currentUser);
  const accountContact = resolveAccountContact(
    isAuthenticated,
    accountDetails,
    currentUser,
  );

  const [step, setStep] = useState<'quantity' | 'details'>(
    skatersInfo.length > 0 ? 'details' : 'quantity',
  );

  const [ticketQuantities, setTicketQuantities] = useState<TicketQuantities>({
    adults: limitAdults && selectedTimeSlot?.adultPrice !== 999 ? 1 : 0, // Start with 1 adult if limited and price is not 999, 0 otherwise
    children: 0,
  });

  const [skaters, setSkaters] = useState<SkaterInfo[]>(
    skatersInfo.length > 0
      ? skatersInfo
      : limitAdults && selectedTimeSlot?.adultPrice !== 999
        ? [
            {
              id: '1',
              firstName: '',
              lastName: '',
              email: accountContact.email,
              phoneNumber: accountContact.phoneNumber,
              dateOfBirth: '',
              isMinor: false,
              isAdult: true,
              isChild: false,
            },
          ]
        : [], // Start with empty array if not limiting adults or if adult price is 999
  );

  const generateSkatersFromQuantities = () => {
    // Check if at least one adult or child is selected
    // Use limitAdults only if adultPrice is not 999
    const adultCount =
      limitAdults && selectedTimeSlot?.adultPrice !== 999
        ? 1
        : ticketQuantities.adults;
    const totalSelected = adultCount + ticketQuantities.children;

    if (totalSelected === 0) {
      // Don't proceed if no skaters are selected
      return;
    }

    const newSkaters: SkaterInfo[] = [];
    let idCounter = 1;

    // Add adult skaters (1 if limitAdults is true and price is not 999, otherwise based on ticketQuantities)
    for (let i = 0; i < adultCount; i++) {
      const isFirstAdult = i === 0;
      newSkaters.push({
        id: idCounter.toString(),
        firstName: '',
        lastName: '',
        email: isFirstAdult ? accountContact.email : '',
        phoneNumber: isFirstAdult ? accountContact.phoneNumber : '',
        dateOfBirth: '',
        isMinor: false,
        isAdult: true,
        isChild: false,
      });
      idCounter++;
    }

    // Add child skaters
    for (let i = 0; i < ticketQuantities.children; i++) {
      newSkaters.push({
        id: idCounter.toString(),
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        isMinor: true,
        isAdult: false,
        isChild: true,
      });
      idCounter++;
    }

    setSkaters(newSkaters);
    setStep('details');
  };

  const updateSkater = (
    id: string,
    field: keyof SkaterInfo,
    value: string | boolean,
  ) => {
    setSkaters(prevSkaters => {
      const updatedSkaters = prevSkaters.map(skater => {
        if (skater.id === id) {
          const updatedSkater = { ...skater, [field]: value };

          // If date of birth is changed, only prepopulate contact info for children if there's an adult with contact info
          if (
            field === 'dateOfBirth' &&
            typeof value === 'string' &&
            value &&
            updatedSkater.isMinor
          ) {
            // Find the first adult with complete contact info
            const adults = prevSkaters.filter(s => !s.isMinor);
            const firstAdultWithInfo = adults.find(
              s => s.email && s.phoneNumber && s.firstName && s.lastName,
            );
            if (firstAdultWithInfo) {
              updatedSkater.email = firstAdultWithInfo.email;
              updatedSkater.phoneNumber = firstAdultWithInfo.phoneNumber;
            }
          }

          return updatedSkater;
        }
        return skater;
      });

      // Auto-inherit contact info whenever any adult completes their contact information
      const updatedSkater = updatedSkaters.find(s => s.id === id);
      if (
        updatedSkater &&
        !updatedSkater.isMinor &&
        (field === 'email' ||
          field === 'phoneNumber' ||
          field === 'firstName' ||
          field === 'lastName')
      ) {
        // Find the first adult with complete contact info (not necessarily the one being updated)
        const adults = updatedSkaters.filter(s => !s.isMinor);
        const firstAdultWithInfo = adults.find(
          s => s.email && s.phoneNumber && s.firstName && s.lastName,
        );

        if (firstAdultWithInfo) {
          return updatedSkaters.map(skater => {
            if (skater.isMinor) {
              return {
                ...skater,
                email: firstAdultWithInfo.email,
                phoneNumber: firstAdultWithInfo.phoneNumber,
              };
            }
            return skater;
          });
        }
      }

      return updatedSkaters;
    });
  };

  // Helper function to get the first adult with complete contact information
  const getFirstAdultWithContactInfo = (): SkaterInfo | null => {
    // Filter adults first, then find the first one with complete contact info
    const adults = skaters.filter(s => !s.isMinor);
    const firstAdultWithInfo = adults.find(
      s => s.email && s.phoneNumber && s.firstName && s.lastName,
    );

    return firstAdultWithInfo || null;
  };

  // Helper function to inherit contact info for all children
  const inheritContactInfoForChildren = () => {
    setSkaters(prevSkaters => {
      // Get the first adult with contact info from the current state
      const adults = prevSkaters.filter(s => !s.isMinor);
      const adultSkater = adults.find(
        s => s.email && s.phoneNumber && s.firstName && s.lastName,
      );

      if (!adultSkater) return prevSkaters;

      return prevSkaters.map(skater => {
        if (skater.isMinor) {
          return {
            ...skater,
            email: adultSkater.email,
            phoneNumber: adultSkater.phoneNumber,
          };
        }
        return skater;
      });
    });
  };

  const handleContinue = () => {
    // Allow proceeding with 0 skaters if limitAdults is false
    if (skaters.length === 0 && !limitAdults) {
      onStepComplete({ skatersInfo: skaters });
      onNext({ skatersInfo: skaters });
      return;
    }

    const isValid = skaters.every(skater => {
      const hasRequiredFields =
        skater.firstName && skater.lastName && skater.dateOfBirth;

      // Email and phone are required for all tickets
      const hasValidContact =
        HelperMethods.isValidEmail(skater.email) &&
        HelperMethods.isValidPhoneNumber(skater.phoneNumber);

      // Age validation based on ticket type
      let hasValidAge = true;
      if (skater.dateOfBirth) {
        if (skater.isMinor) {
          hasValidAge = validateChildAge(skater.dateOfBirth).isValid;
        } else {
          hasValidAge = validateAdultAge(skater.dateOfBirth).isValid;
        }
      }

      // Check if age matches the selected type
      const ageMatchesType = validateAgeMatchesType(
        skater.dateOfBirth,
        skater.isMinor,
      ).isValid;

      return (
        hasRequiredFields && hasValidContact && hasValidAge && ageMatchesType
      );
    });

    if (isValid) {
      onStepComplete({ skatersInfo: skaters });
      onNext({ skatersInfo: skaters });
    }
  };

  const isFormValid =
    // Allow 0 skaters if limitAdults is false
    (skaters.length === 0 && !limitAdults) ||
    // Otherwise, all skaters must be valid
    skaters.every(skater => {
      const hasRequiredFields =
        skater.firstName && skater.lastName && skater.dateOfBirth;

      // Email and phone are required for all tickets
      const hasValidContact =
        HelperMethods.isValidEmail(skater.email) &&
        HelperMethods.isValidPhoneNumber(skater.phoneNumber);

      // Age validation based on ticket type
      let hasValidAge = true;
      if (skater.dateOfBirth) {
        if (skater.isMinor) {
          hasValidAge = validateChildAge(skater.dateOfBirth).isValid;
        } else {
          hasValidAge = validateAdultAge(skater.dateOfBirth).isValid;
        }
      }

      // Check if age matches the selected type
      const ageMatchesType = validateAgeMatchesType(
        skater.dateOfBirth,
        skater.isMinor,
      ).isValid;

      return (
        hasRequiredFields && hasValidContact && hasValidAge && ageMatchesType
      );
    });

  const adultSkaters = skaters.filter(skater => !skater.isMinor);
  const childSkaters = skaters.filter(skater => skater.isMinor);

  if (step === 'quantity') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Select Ticket Quantities
          </h2>
          <p className="text-gray-600">
            How many adult and child tickets do you need?
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-6">
          {/* Adult Tickets */}
          {selectedTimeSlot?.adultPrice !== 999 && (
            <div className="border border-gray-200 rounded-lg p-6 bg-white">
              <div className="flex items-center space-x-3 mb-4">
                <MdPersonOutline className="text-blue-600 w-6 h-6" />
                <h3 className="text-lg font-medium text-gray-900">
                  Adult Tickets
                </h3>
                <div className="text-sm text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                  Ages 13 and older
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Quantity:</span>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() =>
                      setTicketQuantities(prev => ({
                        ...prev,
                        adults: Math.max(limitAdults ? 1 : 0, prev.adults - 1),
                      }))
                    }
                    disabled={limitAdults}
                    className={`w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center ${
                      limitAdults
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    -
                  </button>
                  <span className="w-8 text-center font-medium">
                    {ticketQuantities.adults}
                  </span>
                  <button
                    onClick={() =>
                      setTicketQuantities(prev => ({
                        ...prev,
                        adults: prev.adults + 1,
                      }))
                    }
                    disabled={limitAdults}
                    className={`w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center ${
                      limitAdults
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>
              {limitAdults && (
                <div className="mt-2 text-xs text-gray-500">
                  Limited to 1 adult per booking
                </div>
              )}
            </div>
          )}

          {/* Child Tickets */}
          {selectedTimeSlot?.childPrice !== 999 && (
            <div className="border border-gray-200 rounded-lg p-6 bg-white">
              <div className="flex items-center space-x-3 mb-4">
                <MdChildCare className="text-green-600 w-6 h-6" />
                <h3 className="text-lg font-medium text-gray-900">
                  Child Tickets
                </h3>
                <div className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                  Ages 3-12 years old
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Quantity:</span>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() =>
                      setTicketQuantities(prev => ({
                        ...prev,
                        children: Math.max(0, prev.children - 1),
                      }))
                    }
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    -
                  </button>
                  <span className="w-8 text-center font-medium">
                    {ticketQuantities.children}
                  </span>
                  <button
                    onClick={() =>
                      setTicketQuantities(prev => ({
                        ...prev,
                        children: prev.children + 1,
                      }))
                    }
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-lg font-medium text-gray-900">
              Total Tickets:{' '}
              {(limitAdults && selectedTimeSlot?.adultPrice !== 999
                ? 1
                : ticketQuantities.adults) + ticketQuantities.children}
            </p>
            {selectedTimeSlot && (
              <p className="text-sm text-gray-600 mt-1">
                Available: {selectedTimeSlot.maxCapacity} ticket
                {selectedTimeSlot.maxCapacity !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Capacity Error Warning */}
          {selectedTimeSlot &&
            (limitAdults && selectedTimeSlot.adultPrice !== 999
              ? 1
              : ticketQuantities.adults) +
              ticketQuantities.children >
              selectedTimeSlot.maxCapacity && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <MdWarning className="text-red-600 w-5 h-5 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800 mb-1">
                      Too Many Tickets Selected
                    </h3>
                    <p className="text-sm text-red-700">
                      You have selected{' '}
                      {(limitAdults && selectedTimeSlot.adultPrice !== 999
                        ? 1
                        : ticketQuantities.adults) +
                        ticketQuantities.children}{' '}
                      ticket
                      {(limitAdults && selectedTimeSlot.adultPrice !== 999
                        ? 1
                        : ticketQuantities.adults) +
                        ticketQuantities.children !==
                      1
                        ? 's'
                        : ''}
                      , but only {selectedTimeSlot.maxCapacity}{' '}
                      {selectedTimeSlot.maxCapacity === 1 ? 'is' : 'are'}{' '}
                      currently available for this session. Please adjust the
                      quantity to {selectedTimeSlot.maxCapacity} or fewer.
                    </p>
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row justify-between pt-6 gap-4">
          {onPrevious && (
            <button
              onClick={onPrevious}
              className="px-6 py-3 text-gray-700 bg-gray-200 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors w-full sm:w-auto min-h-[44px] order-2 sm:order-1"
            >
              Back
            </button>
          )}
          <button
            onClick={generateSkatersFromQuantities}
            disabled={
              (limitAdults && selectedTimeSlot?.adultPrice !== 999
                ? 1
                : ticketQuantities.adults) +
                ticketQuantities.children ===
                0 ||
              (selectedTimeSlot &&
                (limitAdults && selectedTimeSlot.adultPrice !== 999
                  ? 1
                  : ticketQuantities.adults) +
                  ticketQuantities.children >
                  selectedTimeSlot.maxCapacity)
            }
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors w-full sm:w-auto min-h-[44px] order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue to Details
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Skater Information
        </h2>
        <p className="text-gray-600">Provide details for each skater</p>
      </div>

      {/* Age Validation Summary */}
      {(() => {
        const ageErrors = skaters
          .map(skater => {
            if (!skater.dateOfBirth) return null;

            if (skater.isMinor) {
              const { isValid, message } = validateChildAge(skater.dateOfBirth);
              return isValid ? null : { skater, message };
            } else {
              const { isValid, message } = validateAdultAge(skater.dateOfBirth);
              return isValid ? null : { skater, message };
            }
          })
          .filter(
            (error): error is { skater: SkaterInfo; message: string } =>
              error !== null,
          );

        if (ageErrors.length > 0) {
          return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <MdWarning className="text-red-600 mr-2" />
                <h3 className="text-red-800 font-medium">
                  Age Validation Errors
                </h3>
              </div>
              <ul className="mt-2 space-y-1">
                {ageErrors.map((error, index) => (
                  <li key={index} className="text-red-700 text-sm">
                    <strong>{error.skater.firstName || 'Skater'}:</strong>{' '}
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return null;
      })()}

      {/* Adult Skaters Section */}
      {adultSkaters.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <MdPersonOutline className="text-blue-600 w-5 h-5" />
            <h3 className="text-lg font-medium text-gray-900">Adult Skaters</h3>
            <div className="text-sm text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
              Ages 13 and older
            </div>
          </div>

          <div className="space-y-4">
            {adultSkaters.map((skater, index) => (
              <div
                key={skater.id}
                className="border border-gray-200 rounded-lg p-4 md:p-6 bg-white"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <MdPerson className="text-blue-600" />
                    <h4 className="text-md font-medium text-gray-900">
                      Adult {index + 1}
                    </h4>
                  </div>
                </div>
                <SkaterForm
                  key={`skater-form-${skater.id}`}
                  skater={skater}
                  updateSkater={updateSkater}
                  allSkaters={skaters}
                  isChild={false}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Child Skaters Section */}
      {childSkaters.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MdChildCare className="text-green-600 w-5 h-5" />
              <h3 className="text-lg font-medium text-gray-900">
                Child Skaters
              </h3>
              <div className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                Ages 3-12 years old
              </div>
              <div className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                Auto-sync enabled
              </div>
            </div>
            {getFirstAdultWithContactInfo() && (
              <button
                onClick={inheritContactInfoForChildren}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
                title="Manually sync contact information from first adult (auto-sync is enabled)"
              >
                Sync Contact Info
              </button>
            )}
          </div>

          <div className="space-y-4">
            {childSkaters.map((skater, index) => (
              <div
                key={skater.id}
                className="border border-gray-200 rounded-lg p-4 md:p-6 bg-green-50"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <MdChildCare className="text-green-600" />
                    <h4 className="text-md font-medium text-gray-900">
                      Child {index + 1}
                    </h4>
                  </div>
                </div>
                <SkaterForm
                  key={`skater-form-${skater.id}`}
                  skater={skater}
                  updateSkater={updateSkater}
                  allSkaters={skaters}
                  isChild={true}
                />
                <div className="mt-2 text-sm text-green-600 flex items-center">
                  <MdChildCare className="mr-1" />
                  Child's contact information will be inherited from the adult
                  ticket holder
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between pt-6 gap-4">
        <button
          onClick={() => setStep('quantity')}
          className="px-6 py-3 text-gray-700 bg-gray-200 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors w-full sm:w-auto min-h-[44px] order-2 sm:order-1"
        >
          Back to Quantities
        </button>
        <button
          onClick={handleContinue}
          disabled={!isFormValid}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto min-h-[44px] order-1 sm:order-2"
        >
          Continue to Waiver
        </button>
      </div>
    </div>
  );
};

// Separate component for the skater form to reduce duplication
interface SkaterFormProps {
  skater: SkaterInfo;
  updateSkater: (
    id: string,
    field: keyof SkaterInfo,
    value: string | boolean,
  ) => void;
  allSkaters: SkaterInfo[];
  isChild?: boolean;
}

const SkaterForm: React.FC<SkaterFormProps> = ({
  skater,
  updateSkater,
  allSkaters,
  isChild = false,
}) => {
  const [dateOfBirthError, setDateOfBirthError] = useState<string | null>(null);

  // Use useEffect to update validation when skater.isMinor changes
  React.useEffect(() => {
    if (skater.dateOfBirth) {
      if (skater.isMinor) {
        const { isValid, message } = validateChildAge(skater.dateOfBirth);
        setDateOfBirthError(isValid ? null : message);
      } else {
        const { isValid, message } = validateAdultAge(skater.dateOfBirth);
        setDateOfBirthError(isValid ? null : message);
      }
    }
  }, [skater.isMinor, skater.dateOfBirth]);

  const handleDateOfBirthChange = (value: string) => {
    updateSkater(skater.id, 'dateOfBirth', value);
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSkater(skater.id, 'firstName', e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSkater(skater.id, 'lastName', e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSkater(skater.id, 'email', e.target.value);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    updateSkater(skater.id, 'phoneNumber', numericValue);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            key={`firstName-${skater.id}`}
            type="text"
            value={skater.firstName}
            onChange={handleFirstNameChange}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              !skater.firstName
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300'
            }`}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            key={`lastName-${skater.id}`}
            type="text"
            value={skater.lastName}
            onChange={handleLastNameChange}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              !skater.lastName
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300'
            }`}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
            {isChild && skater.email && (
              <span className="ml-2 text-xs text-green-600 font-medium">
                (inherited from adult)
              </span>
            )}
          </label>
          <input
            key={`email-${skater.id}`}
            type="email"
            value={skater.email}
            onChange={handleEmailChange}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              !HelperMethods.isValidEmail(skater.email)
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : isChild && skater.email
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
            }`}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
            {isChild && skater.phoneNumber && (
              <span className="ml-2 text-xs text-green-600 font-medium">
                (inherited from adult)
              </span>
            )}
          </label>
          <input
            key={`phoneNumber-${skater.id}`}
            type="tel"
            value={skater.phoneNumber}
            onChange={handlePhoneNumberChange}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              !skater.phoneNumber ||
              !HelperMethods.isValidPhoneNumber(skater.phoneNumber)
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : isChild && skater.phoneNumber
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
            }`}
            placeholder="(555) 123-4567"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date of Birth
          </label>
          <DateOfBirthInput
            value={skater.dateOfBirth}
            onChange={handleDateOfBirthChange}
            min={HelperMethods.getMinDateOfBirth()}
            max={HelperMethods.getMaxDateOfBirth()}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              dateOfBirthError || !skater.dateOfBirth
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300'
            }`}
            required
          />
          {skater.dateOfBirth && (
            <div className="mt-1 text-sm text-gray-600">
              Age: {HelperMethods.calculateAge(skater.dateOfBirth)} years old
            </div>
          )}
          {dateOfBirthError && (
            <div className="mt-1 text-sm text-red-600 flex items-center">
              <MdWarning className="mr-1" />
              {dateOfBirthError}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
